import { artColour, chunkStampsOf, isSubPixel, type AssetManifest, type FrameRect } from "daedalus-core";
import type { ChunkCells } from "./chunk-stamps.ts";

/**
 * The map picture painter (ADR-109's amendment of 2026-10-13, decisions 36 and 38): one function copies a chunk's
 * flat cells and props, scaled down, from the atlas pages already loaded into a rectangle of a 2D canvas. It
 * allocates no chunk texture and holds no state: it knows neither the editor's document nor a compiled chunk, only
 * the cells and props it is handed, so the editor feeds it from its own layers and the game page from a client chunk.
 * The canvas and the atlas are reached through the small interfaces below, so the module imports no browser type
 * beyond the canvas value types and runs against a stand-in in tests.
 */

/**
 * What the painter asks of a 2D canvas context: the subset of `CanvasRenderingContext2D` it uses, so a real context
 * and a test stand-in both fit. `I` is the type of an atlas page's image.
 */
export interface PictureContext<I> {
  imageSmoothingEnabled: boolean;
  imageSmoothingQuality: ImageSmoothingQuality;
  fillStyle: string | CanvasGradient | CanvasPattern;
  save(): void;
  restore(): void;
  beginPath(): void;
  rect(x: number, y: number, w: number, h: number): void;
  clip(): void;
  clearRect(x: number, y: number, w: number, h: number): void;
  fillRect(x: number, y: number, w: number, h: number): void;
  setTransform(a: number, b: number, c: number, d: number, e: number, f: number): void;
  drawImage(image: I, sx: number, sy: number, sw: number, sh: number, dx: number, dy: number, dw: number, dh: number): void;
}

/**
 * A canvas a picture is composed on: its size, its context and a way to hear that the browser lost the context and gave it back.
 */
export interface PictureSurface<I> {
  readonly width: number;
  readonly height: number;
  readonly context: PictureContext<I>;
  onLost(listener: () => void): void;
  onRestored(listener: () => void): void;
  dispose(): void;
}

/**
 * The atlas pages the painter copies from: `image` gives a page's source image, or null while it is not loaded, and
 * `meanColour` the mean colour of a frame's opaque pixels (null when it has none), which an atlas that gives one
 * keeps once a frame. An atlas without it has every prop scaled, however small.
 */
export interface PictureAtlas<I> {
  image(page: number): I | null;
  meanColour?(frame: string, page: number, rect: FrameRect): { readonly r: number; readonly g: number; readonly b: number } | null;
}

/**
 * One prop to draw: its frame id, the cell its bottom-left stands on, in map tiles, and its mirroring.
 */
export interface PictureProp {
  readonly frame: string;
  readonly x: number;
  readonly y: number;
  readonly flipX: boolean;
  readonly flipY: boolean;
}

/**
 * One layer of a chunk, in drawn order: the cells of a tile or terrain layer, or the props of a prop layer that
 * reach into the chunk, already in the order they draw. A layer whose `visible` is false is left out.
 */
export type PictureLayer =
  | { readonly kind: "cells"; readonly visible?: boolean; readonly cells: ChunkCells }
  | { readonly kind: "props"; readonly visible?: boolean; readonly props: readonly PictureProp[] };

/**
 * The two CSS colours of the placeholder ground an undrawn chunk shows (decision 43): a flat ground and the dot laid
 * at every fourth tile. The caller chooses them, from the canvas colour table in the editor.
 */
export interface PicturePlaceholder {
  readonly ground: string;
  readonly dot: string;
}

/**
 * One chunk to paint. The chunk lands at `originX` and `originY` in the picture, its own place at `scale` pixels a
 * tile when they are omitted. `placeholder` lays the placeholder ground under the drawing.
 */
export interface PictureChunk {
  readonly cx: number;
  readonly cy: number;
  readonly chunkSize: number;
  readonly tileSize: number;
  readonly scale: number;
  readonly manifest: AssetManifest;
  readonly layers: readonly PictureLayer[];
  readonly originX?: number;
  readonly originY?: number;
  readonly placeholder?: PicturePlaceholder;
}

/**
 * What a paint did: frames drawn, draws left undone because their atlas page was not loaded, and cells and props
 * skipped because the manifest lacks their frame.
 */
export interface PictureResult {
  readonly drawn: number;
  readonly unavailable: number;
  readonly missing: number;
}

/**
 * Tiles between the placeholder's dots.
 */
export const PLACEHOLDER_DOT_STEP = 4;

/**
 * Draws one frame into a rectangle, mirrored about the rectangle when asked, and leaves the transform as the
 * identity.
 *
 * @param context The context.
 * @param image The page image.
 * @param rect The frame's inner rectangle on the page.
 * @param dx Destination left.
 * @param dy Destination top.
 * @param dw Destination width.
 * @param dh Destination height.
 * @param flipX Whether mirrored left to right.
 * @param flipY Whether mirrored top to bottom.
 */
function drawFrame<I>(context: PictureContext<I>, image: I, rect: FrameRect, dx: number, dy: number, dw: number, dh: number, flipX: boolean, flipY: boolean): void {
  if (!flipX && !flipY) {
    context.drawImage(image, rect.x, rect.y, rect.w, rect.h, dx, dy, dw, dh);
    return;
  }
  context.setTransform(flipX ? -1 : 1, 0, 0, flipY ? -1 : 1, flipX ? dx + dw : dx, flipY ? dy + dh : dy);
  context.drawImage(image, rect.x, rect.y, rect.w, rect.h, 0, 0, dw, dh);
  context.setTransform(1, 0, 0, 1, 0, 0);
}

/**
 * Lays the placeholder ground over a rectangle: one flat colour and a dot at every fourth tile.
 *
 * @param context The context.
 * @param placeholder The two colours.
 * @param x Left.
 * @param y Top.
 * @param size The rectangle's side in pixels.
 * @param scale Pixels a tile.
 */
function drawPlaceholder<I>(context: PictureContext<I>, placeholder: PicturePlaceholder, x: number, y: number, size: number, scale: number): void {
  context.fillStyle = placeholder.ground;
  context.fillRect(x, y, size, size);
  context.fillStyle = placeholder.dot;
  const step = PLACEHOLDER_DOT_STEP * scale;
  for (let dy = 0; dy < size; dy += step) {
    for (let dx = 0; dx < size; dx += step) {
      context.fillRect(x + dx, y + dy, scale, scale);
    }
  }
}

/**
 * Paints one chunk of a map into a canvas at a scale: clears the chunk's rectangle, clips to it, and draws each
 * visible layer in order, the cells of a tile layer from the packed tiles and the props of a prop layer by their
 * bottom-left anchors, each frame scaled from its inner rectangle on its atlas page. A cell or prop whose frame the
 * manifest lacks is skipped and counted, and a draw whose page is not loaded is left undone and counted, so a caller
 * can try the chunk again later. Nothing outside the chunk's rectangle is touched, so a prop standing in the next
 * chunk is drawn by that chunk's own paint.
 *
 * @param context The canvas context.
 * @param atlas The loaded atlas pages.
 * @param chunk The chunk, its layers and the scale.
 * @returns What was drawn, left undone and skipped.
 * @throws Error when a cell names a tileset index its chunk does not list, or its cell count is wrong.
 */
export function paintPictureChunk<I>(context: PictureContext<I>, atlas: PictureAtlas<I>, chunk: PictureChunk): PictureResult {
  const { scale, tileSize, chunkSize, manifest } = chunk;
  const factor = scale / tileSize;
  const size = chunkSize * scale;
  const originX = chunk.originX ?? chunk.cx * size;
  const originY = chunk.originY ?? chunk.cy * size;
  let drawn = 0;
  let unavailable = 0;
  let missing = 0;
  context.save();
  context.setTransform(1, 0, 0, 1, 0, 0);
  context.clearRect(originX, originY, size, size);
  context.beginPath();
  context.rect(originX, originY, size, size);
  context.clip();
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  if (chunk.placeholder !== undefined) {
    drawPlaceholder(context, chunk.placeholder, originX, originY, size, scale);
  }
  const pageOf = (page: number): I | null => atlas.image(page);
  for (const layer of chunk.layers) {
    if (layer.visible === false) {
      continue;
    }
    if (layer.kind === "cells") {
      const cells: ChunkCells = { ...layer.cells, missing: (cell) => {
        missing += 1;
        layer.cells.missing?.(cell);
      } };
      chunkStampsOf(cells, (page, _frame, rect, x, y, flipX, flipY) => {
        const image = pageOf(page);
        if (image === null) {
          unavailable += 1;
          return;
        }
        drawFrame(context, image, rect, originX + x * factor, originY + y * factor, rect.w * factor, rect.h * factor, flipX, flipY);
        drawn += 1;
      });
    } else {
      for (const prop of layer.props) {
        const rect = manifest.frames[prop.frame];
        if (rect === undefined) {
          missing += 1;
          continue;
        }
        const image = pageOf(rect.page);
        if (image === null) {
          unavailable += 1;
          continue;
        }
        const left = originX + (prop.x - chunk.cx * chunkSize) * scale;
        const bottom = originY + (prop.y + 1 - chunk.cy * chunkSize) * scale;
        if (atlas.meanColour !== undefined && isSubPixel(rect.w, rect.h, factor)) {
          const mean = atlas.meanColour(prop.frame, rect.page, rect);
          if (mean !== null) {
            context.fillStyle = artColour(mean);
            context.fillRect(Math.floor(left + (rect.w * factor) / 2), Math.round(bottom) - 1, 1, 1);
            drawn += 1;
          }
          continue;
        }
        drawFrame(context, image, rect, left, bottom - rect.h * factor, rect.w * factor, rect.h * factor, prop.flipX, prop.flipY);
        drawn += 1;
      }
    }
  }
  context.restore();
  return { drawn, unavailable, missing };
}
